import getNewElementDisplay from 'editor/src/store/design/operation/getNewElementDisplay';
import getNewImageDisplaySize from 'editor/src/store/design/operation/getNewImageDisplaySize';
import hasUsedImagesInSpread from 'editor/src/store/design/selector/hasUsedImagesInSpread';
import { Dimensions, MediaImage } from 'editor/src/store/design/types';
import getCurrentSpreadIndex from 'editor/src/store/editor/selector/getCurrentSpreadIndex';
import getDefaultPersonalizationLockState from 'editor/src/store/editor/selector/getDefaultPersonalizationLockState';
import { AssetDigitizationStatus } from 'editor/src/store/gallery/types';
import { GANG_SHEET_DEFAULT_IMAGE_DPI } from 'editor/src/store/gangSheet/constants';
import getImageSizeAtDpiMm from 'editor/src/store/gangSheet/util/getImageSizeAtDpiMm';
import shouldDigitizeSpreadIndex from 'editor/src/store/utils/shouldDigitizeSpreadIndex';

import { RootState } from 'editor/src/store';

import getDigitizedSizeMm from './2d/getDigitizedSizeMm';
import applyFillImageToSpread from './design/applyFillImageToSpread';
import applyFitImageToSpread from './design/applyFitImageToSpread';
import getPointPositionRotatedOnPoint from './getPointPositionRotatedOnPoint';
import { elementUuids } from './uuids';

const getNewImage = (
  state: RootState,
  image: {
    id: string;
    name: string;
    url: string;
    width: number;
    height: number;
    digitizationStatus?: AssetDigitizationStatus;
    digitizedSizeMm?: Dimensions;
  },
  positioning: 'fill' | 'fit' | undefined = 'fill',
  spreadIndex: number = getCurrentSpreadIndex(state),
  uuid: number = elementUuids.generate(),
  x?: number,
  y?: number,
  shiftX?: number,
  shiftY?: number,
): MediaImage | undefined => {
  const display = getNewElementDisplay(
    state,
    spreadIndex,
    (area) => getNewImageDisplaySize(area, image.width, image.height),
    x,
    y,
  );
  if (!display) {
    return undefined;
  }

  const { center, rotation, width, height, area } = display;

  const defaultPersonalizationLockState = getDefaultPersonalizationLockState(state);

  let newImage: MediaImage = {
    imageId: image.id,
    url: image.url,
    type: 'image',
    group: '',
    name: image.name,
    x: center.x - (shiftX || 0),
    y: center.y - (shiftY || 0),
    width,
    height,
    r: rotation,
    px: 0,
    py: 0,
    pw: width,
    ph: height,
    pr: 0,
    uuid,
    personalizationLocked: defaultPersonalizationLockState || undefined,
  };

  const isDigitizedImage =
    image.digitizationStatus === AssetDigitizationStatus.DIGITIZED && shouldDigitizeSpreadIndex(state, spreadIndex);
  if (isDigitizedImage) {
    // keep the digitized aspect ratio and physical size (when known) instead of filling the
    // spread, so the existing digitization is reused instead of triggering a new digitization fee
    const digitizedSize = getDigitizedSizeMm(image.digitizedSizeMm, { width: image.width, height: image.height });
    if (digitizedSize) {
      // place at the resolved digitized size, centered in the area (elements rotate around their
      // top-left, so rotate the half-extent to keep the element visually centered)
      const [centerOffsetX, centerOffsetY] = getPointPositionRotatedOnPoint(
        digitizedSize.width / 2,
        digitizedSize.height / 2,
        0,
        0,
        rotation,
      );
      newImage = {
        ...newImage,
        width: digitizedSize.width,
        height: digitizedSize.height,
        pw: digitizedSize.width,
        ph: digitizedSize.height,
        px: 0,
        py: 0,
        x: area.left + area.width / 2 - centerOffsetX,
        y: area.top + area.height / 2 - centerOffsetY,
      };
    }
  } else if (state.hostSettings.gangSheet) {
    // gang sheet artwork defaults to its physical size at the configured print DPI
    // instead of filling the sheet — stretching a small asset over a large sheet
    // yields unusable print resolution (host-gated: only gang sheet products)
    const dpi = state.hostSettings.gangSheet.defaultImageDpi ?? GANG_SHEET_DEFAULT_IMAGE_DPI;
    const dpiSize = getImageSizeAtDpiMm(image.width, image.height, dpi, area);
    if (dpiSize) {
      const [centerOffsetX, centerOffsetY] = getPointPositionRotatedOnPoint(
        dpiSize.width / 2,
        dpiSize.height / 2,
        0,
        0,
        rotation,
      );
      newImage = {
        ...newImage,
        width: dpiSize.width,
        height: dpiSize.height,
        pw: dpiSize.width,
        ph: dpiSize.height,
        px: 0,
        py: 0,
        x: area.left + area.width / 2 - centerOffsetX,
        y: area.top + area.height / 2 - centerOffsetY,
      };
    }
  } else if (positioning) {
    const imageRatio = width / height;
    const areaRatio = area.width / area.height;
    const hasUsedImages = hasUsedImagesInSpread(state, spreadIndex);
    const ratioSimilarity = Math.abs(imageRatio - areaRatio);

    if (!hasUsedImages && ratioSimilarity <= 0.2) {
      const imageUpdate =
        positioning === 'fit' ? applyFitImageToSpread(newImage, area) : applyFillImageToSpread(newImage, area);
      newImage = { ...newImage, ...imageUpdate };
    }
  }

  return newImage;
};

export default getNewImage;
